'use client';

import * as React from 'react';
import * as M from '@radix-ui/react-dropdown-menu';
import { Check, ChevronLeft, Circle } from 'lucide-react';
import { cn } from './cn';

export const DropdownMenuRoot = M.Root;
export const DropdownMenuTrigger = M.Trigger;
export const DropdownMenuGroup = M.Group;
export const DropdownMenuRadioGroup = M.RadioGroup;
export const DropdownMenuSub = M.Sub;

const SURFACE = cn(
  'z-(--z-popover) min-w-[11rem] overflow-y-auto rounded-lg border border-line bg-surface p-1 shadow-lg',
  'max-h-(--radix-dropdown-menu-content-available-height)',
  'origin-(--radix-dropdown-menu-content-transform-origin)',
  'data-[state=open]:animate-scale-in data-[state=closed]:animate-scale-out',
);

const ITEM = cn(
  'relative flex min-h-touch cursor-pointer select-none items-center gap-2.5 rounded-sm',
  'px-2.5 py-2 text-sm text-ink outline-none sm:min-h-9',
  'data-[highlighted]:bg-paper-3 data-[highlighted]:text-ink',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
  '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-3',
  'data-[highlighted]:[&_svg]:text-ink-2',
);

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof M.Content>,
  React.ComponentPropsWithoutRef<typeof M.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, collisionPadding = 12, ...props }, ref) {
  return (
    <M.Portal>
      <M.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(SURFACE, className)}
        {...props}
      />
    </M.Portal>
  );
});

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<typeof M.Item> {
  icon?: React.ReactNode;
  /** Right-hand hint, usually a `<Kbd>`. */
  shortcut?: React.ReactNode;
  destructive?: boolean;
}

export const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof M.Item>, DropdownMenuItemProps>(
  function DropdownMenuItem({ className, icon, shortcut, destructive, children, asChild, ...props }, ref) {
    const decorate = (label: React.ReactNode) => (
      <>
        {icon}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {shortcut ? <span className="ms-auto text-ink-3">{shortcut}</span> : null}
      </>
    );

    // With `asChild` the item renders as the caller's own element (a <Link>, say) and Radix's Slot
    // accepts exactly one child. Icon and shortcut must therefore move *inside* that element instead
    // of sitting beside it, or Slot throws React.Children.only.
    const content = asChild
      ? React.cloneElement(
          React.Children.only(children) as React.ReactElement<{ children?: React.ReactNode }>,
          undefined,
          decorate((React.Children.only(children) as React.ReactElement<{ children?: React.ReactNode }>).props.children),
        )
      : decorate(children);

    return (
      <M.Item
        ref={ref}
        asChild={asChild}
        className={cn(
          ITEM,
          destructive && 'text-danger data-[highlighted]:bg-danger-soft data-[highlighted]:text-danger [&_svg]:text-danger',
          className,
        )}
        {...props}
      >
        {content}
      </M.Item>
    );
  },
);

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof M.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof M.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <M.CheckboxItem ref={ref} className={cn(ITEM, 'ps-8', className)} {...props}>
      <M.ItemIndicator className="absolute start-2 flex">
        <Check className="size-4 text-accent" />
      </M.ItemIndicator>
      {children}
    </M.CheckboxItem>
  );
});

export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof M.RadioItem>,
  React.ComponentPropsWithoutRef<typeof M.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <M.RadioItem ref={ref} className={cn(ITEM, 'ps-8', className)} {...props}>
      <M.ItemIndicator className="absolute start-2 flex">
        <Circle className="size-2 fill-accent text-accent" />
      </M.ItemIndicator>
      {children}
    </M.RadioItem>
  );
});

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof M.Label>,
  React.ComponentPropsWithoutRef<typeof M.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <M.Label
      ref={ref}
      className={cn('text-2xs px-2.5 pt-2 pb-1 font-semibold tracking-wide text-ink-3 uppercase', className)}
      {...props}
    />
  );
});

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof M.Separator>,
  React.ComponentPropsWithoutRef<typeof M.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return <M.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-line', className)} {...props} />;
});

export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof M.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof M.SubTrigger> & { icon?: React.ReactNode }
>(function DropdownMenuSubTrigger({ className, icon, children, ...props }, ref) {
  return (
    <M.SubTrigger ref={ref} className={cn(ITEM, 'data-[state=open]:bg-paper-3', className)} {...props}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {/* points toward the reading end: left in Hebrew, flipped in English */}
      <ChevronLeft className="k-flip-rtl ms-auto" />
    </M.SubTrigger>
  );
});

export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof M.SubContent>,
  React.ComponentPropsWithoutRef<typeof M.SubContent>
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return (
    <M.Portal>
      <M.SubContent ref={ref} className={cn(SURFACE, className)} {...props} />
    </M.Portal>
  );
});

/** Namespaced access: `<DropdownMenu.Root>`, `<DropdownMenu.Item>`, … */
export const DropdownMenu = {
  Root: M.Root,
  Trigger: M.Trigger,
  Content: DropdownMenuContent,
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: M.RadioGroup,
  RadioItem: DropdownMenuRadioItem,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Group: M.Group,
  Sub: M.Sub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
};
